import { useEffect, useRef, useState } from 'react';

import type { PresenceSpeechEnvelope } from '@/common/components/presence/types';
import { sampleFakeSpeechAmp } from '@/visual-lab/chapters/orb/fakeSpeechProfile';

/** Match production analyser peak-hold so lab punch feels like a live track. */
const PUNCH_DECAY = 0.93;

const ZERO: PresenceSpeechEnvelope = { amp: 0, punch: 0 };

/**
 * rAF-driven fake speech envelope for Visual Lab only.
 * Production orbs never call this — they analyse `trackRef`.
 */
export function useFakeSpeechEnvelope(enabled: boolean): PresenceSpeechEnvelope | undefined {
  const [envelope, setEnvelope] = useState<PresenceSpeechEnvelope>(ZERO);
  const punchRef = useRef(0);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      punchRef.current = 0;
      startRef.current = null;
      setEnvelope(ZERO);
      return undefined;
    }

    let raf = 0;
    const tick = (now: number) => {
      if (startRef.current == null) startRef.current = now;
      const amp = sampleFakeSpeechAmp(now - startRef.current);
      punchRef.current = Math.max(amp, punchRef.current * PUNCH_DECAY);
      setEnvelope({ amp, punch: punchRef.current });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [enabled]);

  if (!enabled) return undefined;
  return envelope;
}
